import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_LABELS = {
  interest_food: 'Food & Family Support',
  interest_hygiene: 'Hygiene & Wellness',
  interest_youth: 'Youth Programs',
  interest_safety: 'Safety & Emergency Prep',
  interest_events: 'Community Events',
  interest_education: 'Education & Career',
  interest_relationships: 'Relationship Building',
};

const CommunityInterestInsightsCard = ({ days = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      // Backend max is 365 — if range filter is "All time" (0), pass 365 as a sensible default.
      params.set('days', String(days && days > 0 ? days : 365));
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/insights/community-interest?${params}`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load community interest insights.');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex justify-center" data-testid="community-interest-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="community-interest-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-[#0A0A0A] inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#BA1826]" /> Top requested programs
        </p>
        <button onClick={load} className="text-[10px] text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1"
          data-testid="community-interest-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {error && <p className="text-[11px] text-red-700 mb-2">{error}</p>}
      {data && data.total_logs_with_interest === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic">
          No structured interest survey responses yet in this range. As volunteers complete the
          Community Interest Survey out in the field, this card will show what neighbors are
          asking for most.
        </p>
      ) : data ? (
        <>
          <p className="text-[10px] text-[#9CA3AF] mb-2">
            Based on {data.total_logs_with_interest} neighbor{data.total_logs_with_interest === 1 ? '' : 's'} who responded · last {data.days} days
          </p>

          {/* Overall top 5 across all categories */}
          {data.top_overall && data.top_overall.length > 0 && (
            <div className="mb-3" data-testid="community-interest-top-overall">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Top asks across all categories</p>
              <ul className="space-y-1">
                {data.top_overall.slice(0, 5).map((row, i) => {
                  const max = data.top_overall[0]?.count || 1;
                  const pct = Math.round((row.count / max) * 100);
                  return (
                    <li key={`${row.question_id}-${row.option}`} data-testid={`community-interest-top-${i}`}>
                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                        <span className="text-[#0A0A0A] truncate pr-2">
                          <span className="text-[9px] text-[#9CA3AF] mr-1">{QUESTION_LABELS[row.question_id] || row.question_id}</span>
                          {row.option}
                        </span>
                        <span className="text-[#4B5563] font-semibold">{row.count}</span>
                      </div>
                      <div className="h-1.5 bg-[#F1F5F9] rounded overflow-hidden">
                        <div className="h-full bg-[#BA1826]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Top priority */}
          {data.top_priority && data.top_priority.length > 0 && (
            <div className="mb-2" data-testid="community-interest-top-priority">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">What matters most right now?</p>
              <div className="flex flex-wrap gap-1">
                {data.top_priority.slice(0, 7).map((row) => (
                  <span key={row.option}
                    className="text-[10px] px-2 py-0.5 bg-[#FFF8F8] border border-[#BA1826]/30 text-[#BA1826] rounded-full font-semibold">
                    {row.option} · {row.count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent write-ins */}
          {data.write_ins && data.write_ins.length > 0 && (
            <details className="text-[11px]" data-testid="community-interest-writeins">
              <summary className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] cursor-pointer">
                Write-in answers ({data.write_ins.length})
              </summary>
              <ul className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
                {data.write_ins.slice(-10).reverse().map((w, i) => (
                  <li key={i} className="text-[#4B5563] italic border-l-2 border-[#E5E7EB] pl-2">&ldquo;{w}&rdquo;</li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : null}
    </div>
  );
};

export default CommunityInterestInsightsCard;
